import { AutorespondersInput } from '@pypestream/api-services/urql';
import {
  Button,
  Card,
  Icon,
  IconButton,
  Image,
  PageSection,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectOption,
  Snackbar,
  Spacer,
  Stack,
  TextBody,
  TextSubtitle,
  TextTitle,
  Toggle,
  Input,
} from '@pypestream/design-system';
import { FC, useEffect, useMemo, useState } from 'react';

import { SettingsEmptyState } from '../components/settings-empty-state';
import { SettingsSectionLoader } from '../components/settings-section-loader';
import { StudioPageProps, useLoadingState } from '../hooks';
import {
  describeConnection,
  getChannelsFromPypes,
  getEnvDotStyle,
  getPlatformLogo,
  isContactCenterPlatform,
} from '../utils';
import { WithLoadingState } from '../router';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../xstate/app.xstate';
import { Pype } from '../xstate/prostudio.api-service.types';

type Env = 'live' | 'sandbox';
type MessageKey = 'busy' | 'offline' | 'technicalError';

interface MessageState {
  enable: boolean;
  text: string;
}

type PlatformMessages = Record<MessageKey, MessageState>;

const EMPTY_MESSAGE: MessageState = { enable: false, text: '' };
const EMPTY_PLATFORM_MESSAGES: PlatformMessages = {
  busy: EMPTY_MESSAGE,
  offline: EMPTY_MESSAGE,
  technicalError: EMPTY_MESSAGE,
};

const MESSAGE_FIELDS: { key: MessageKey; label: string }[] = [
  { key: 'offline', label: 'Agents offline' },
  { key: 'busy', label: 'Agents busy' },
  { key: 'technicalError', label: 'Technical error' },
];

const toPlatformMessages = (pype: Pype): PlatformMessages => ({
  offline: {
    enable: pype.autoresponders?.offline?.enable ?? false,
    text: pype.autoresponders?.offline?.text ?? '',
  },
  busy: {
    enable: pype.autoresponders?.busy?.enable ?? false,
    text: pype.autoresponders?.busy?.text ?? '',
  },
  technicalError: {
    enable: pype.autoresponders?.technical_error?.enable ?? false,
    text: pype.autoresponders?.technical_error?.text ?? '',
  },
});

const toAutorespondersInput = (
  messages: PlatformMessages
): AutorespondersInput => ({
  busy: messages.busy,
  offline: messages.offline,
  technicalError: messages.technicalError,
});

const MessageField: FC<{
  label: string;
  value: MessageState;
  disabled?: boolean;
  testId: string;
  onValueChange: (value: MessageState) => void;
}> = ({ label, value, disabled, testId, onValueChange }) => (
  <>
    <Stack alignItems="center" justifyContent="space-between" nowrap>
      <TextBody size="small">{label}</TextBody>
      <Toggle
        test-id={`${testId}-enable`}
        checked={value.enable}
        disabled={disabled}
        onChange={(e) => onValueChange({ ...value, enable: e.target.checked })}
      />
    </Stack>
    <Spacer size="2xsmall" />
    <Input
      test-id={testId}
      variant="outlined"
      value={value.text}
      disabled={disabled}
      onChange={(e) =>
        onValueChange({ ...value, text: (e.target as HTMLInputElement).value })
      }
      onInput={(e) =>
        onValueChange({ ...value, text: (e.target as HTMLInputElement).value })
      }
    />
  </>
);

const PlatformSection: FC<{
  pype: Pype;
  messages: PlatformMessages;
  onMessageChange: (key: MessageKey, value: MessageState) => void;
}> = ({ pype, messages, onMessageChange }) => {
  const [expanded, setExpanded] = useState(true);
  const managedExternally = isContactCenterPlatform(pype.agent_routing);

  // Name the card after the channel, not its pype: the pype name is a derived
  // `{bot}-{channel}` slug ("Pypestreampmremlsernjrzfhatguooche-test123") that
  // appears nowhere else in the app. Fall back to it only when no channel can be
  // derived (a pype with no widget/voice stream), so the card is never unlabelled.
  const channel = useMemo(() => getChannelsFromPypes([pype])[0], [pype]);
  const title = channel?.name || pype.name;
  // Greyed-out identity of the connection behind it, per Figma
  // ("Account ID: 142 Inbox ID 1111", "hbomax.my.salesforce.com").
  const connection = describeConnection(pype.agent_routing_config);

  return (
    <Card
      size="medium"
      border="default"
      shadow="none"
      test-id={`ps-default-messages-platform-${pype.id}`}
    >
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <Stack alignItems="center" gutter="xsmall" nowrap>
          <Image
            src={getPlatformLogo(pype.agent_routing)}
            width="24px"
            height="24px"
          />
          <TextSubtitle size="small">{title}</TextSubtitle>
          {connection && (
            <TextBody size="small" variant="tertiary">
              {connection}
            </TextBody>
          )}
        </Stack>
        <IconButton
          test-id={`ps-default-messages-platform-${pype.id}-toggle`}
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
        />
      </Stack>

      {expanded && (
        <>
          {managedExternally && (
            <>
              <Spacer size="medium" />
              {/* Notice box per Figma: purple-300 @ 10% fill, purple-300
                  border, purple-500 info icon. */}
              <div
                style={{
                  background: 'rgba(133, 115, 231, 0.1)',
                  border: '1px solid var(--purple-300, #8573e7)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                }}
              >
                <Stack alignItems="center" gutter="xsmall" nowrap>
                  <Icon
                    name="info"
                    size="small"
                    style={{
                      ['--icon-color' as string]: 'var(--purple-500, #5956e3)',
                    }}
                  />
                  <TextBody size="small">
                    Auto-response copy is managed at the inbox level in Contact
                    Center and is not configurable here.
                  </TextBody>
                </Stack>
              </div>
            </>
          )}

          {MESSAGE_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <Spacer size="medium" />
              <MessageField
                label={label}
                value={messages[key]}
                disabled={managedExternally}
                testId={`ps-default-messages-platform-${pype.id}-${key}`}
                onValueChange={(value) => onMessageChange(key, value)}
              />
            </div>
          ))}
        </>
      )}
    </Card>
  );
};

const DefaultMessagesPage: FC<StudioPageProps> = () => {
  const [env, setEnv] = useState<Env>('sandbox');
  const [language, setLanguage] = useState('en');
  const [draft, setDraft] = useState<Record<string, PlatformMessages>>({});
  const [touchedIds, setTouchedIds] = useState<Set<string>>(new Set());

  const defaultMessagesPypes = useProStudioCtxSelector(
    (ctx) => ctx.defaultMessagesPypes
  );

  // Load once. See live-agent-platforms.tsx for why this is gated on
  // useLoadingState() too, not just hasLoaded.
  const isPageLoading = useLoadingState();
  const hasLoaded = defaultMessagesPypes != null;
  useEffect(() => {
    if (!isPageLoading && !hasLoaded) {
      sendProStudioEvent({ type: 'loadDefaultMessages' });
    }
  }, [isPageLoading, hasLoaded]);

  const platforms = defaultMessagesPypes?.[env] ?? [];

  // Reset the draft to the loaded values whenever the source data or env
  // changes, discarding any in-progress edits for the previous env.
  useEffect(() => {
    setDraft(
      Object.fromEntries(
        platforms.map((pype) => [pype.id, toPlatformMessages(pype)])
      )
    );
    setTouchedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMessagesPypes, env]);

  const handleMessageChange = (
    pypeId: string,
    key: MessageKey,
    value: MessageState
  ) => {
    setDraft((prev) => ({
      ...prev,
      [pypeId]: { ...(prev[pypeId] ?? EMPTY_PLATFORM_MESSAGES), [key]: value },
    }));
    setTouchedIds((prev) => new Set(prev).add(pypeId));
  };

  const handleCancel = () => {
    setDraft(
      Object.fromEntries(
        platforms.map((pype) => [pype.id, toPlatformMessages(pype)])
      )
    );
    setTouchedIds(new Set());
  };

  const handleSave = () => {
    // The machine handles `updateDefaultMessages` only in `idle`, and the first
    // event transitions it out of `idle` — so dispatching one per channel in a
    // synchronous loop had xstate silently drop every save after the first:
    // only one channel persisted, the rest stayed dirty, and the success toast
    // fired anyway. Chain each save off the previous one's callback, the same
    // way `channel-settings-form` serializes its own idle-only flows.
    //
    // A failed save never invokes its callback, so the chain stops there rather
    // than reporting the remaining channels as saved.
    const saves = [...touchedIds]
      .filter((pypeId) => pypeId && draft[pypeId])
      .map((pypeId) => (next?: () => void) => {
        sendProStudioEvent({
          type: 'updateDefaultMessages',
          payload: {
            id: pypeId,
            env,
            autoresponders: toAutorespondersInput(draft[pypeId]!),
            cb: () => {
              setTouchedIds((prev) => {
                const remaining = new Set(prev);
                remaining.delete(pypeId);
                return remaining;
              });
              next?.();
            },
          },
        });
      });

    saves.reduceRight<(() => void) | undefined>(
      (next, dispatch) => () => dispatch(next),
      undefined
    )?.();
  };

  return (
    <>
      <Stack alignItems="center" justifyContent="space-between" nowrap>
        <TextTitle test-id="ps-default-messages-page-title" size="small">
          Default Messages
        </TextTitle>

        <Stack alignItems="center" gutter="medium" nowrap>
          <SegmentedControl
            test-id="ps-default-messages-env"
            fitContent
            value={env}
            onChange={(e) => setEnv(e.detail.value as Env)}
          >
            <SegmentedControlItem value="sandbox">
              <span
                slot="prefix"
                style={getEnvDotStyle('sandbox', env === 'sandbox')}
              />
              Sandbox
            </SegmentedControlItem>
            <SegmentedControlItem value="live">
              <span
                slot="prefix"
                style={getEnvDotStyle('live', env === 'live')}
              />
              Live
            </SegmentedControlItem>
          </SegmentedControl>

          <Select
            test-id="ps-default-messages-language"
            variant="outlined"
            value={language}
            onChange={(e) =>
              setLanguage((e.target as HTMLSelectElement).value as string)
            }
          >
            <SelectOption value="en">English - American (en)</SelectOption>
          </Select>
        </Stack>
      </Stack>

      <Spacer size="xlarge" />

      <PageSection size="small" test-id="ps-default-messages-platforms-section">
        <TextTitle size="xsmall">Live agent Platform</TextTitle>
        <Spacer size="medium" />
        {!hasLoaded ? (
          <SettingsSectionLoader testId="ps-default-messages-loading" />
        ) : platforms.length === 0 ? (
          // Unlike the Project Settings lists, an empty list here doesn't mean
          // "you haven't created one" — Default Messages are per channel, so the
          // fix is to add a channel to this solution, not anything on this screen.
          <SettingsEmptyState
            testId="ps-default-messages-empty"
            title="No channels in this environment"
            description={`Add a ${env === 'live' ? 'Live' : 'Sandbox'} channel to this solution to set its default messages.`}
          />
        ) : (
          <Stack direction="column" gutter="medium" alignItems="stretch">
            {platforms.map((pype) => (
              <PlatformSection
                key={pype.id}
                pype={pype}
                messages={draft[pype.id ?? ''] ?? toPlatformMessages(pype)}
                onMessageChange={(key, value) =>
                  pype.id && handleMessageChange(pype.id, key, value)
                }
              />
            ))}
          </Stack>
        )}
      </PageSection>

      <Snackbar
        test-id="ps-default-messages-snackbar"
        open={touchedIds.size > 0}
      >
        There are unsaved changes
        <Button
          test-id="ps-default-messages-cancel"
          variant="secondary"
          size="large"
          onClick={handleCancel}
          slot="action"
        >
          Cancel
        </Button>
        <Button
          test-id="ps-default-messages-save"
          size="large"
          onClick={handleSave}
          slot="action"
        >
          Save Changes
        </Button>
      </Snackbar>
    </>
  );
};

export const DefaultMessages = WithLoadingState(DefaultMessagesPage);
